import { ICarrierCapacity, IRate } from 'types/contractLanes'
import { DayOfWeek } from './datetime'

export type TCarrierStatus =
  | 'activated'
  | 'new'
  | 'pending'
  | 'application_submitted'
  | 'in_review'
  | 'expired_insurance'
  | 'out_of_service'
  | 'conditional'
  | 'cancelled'
  | 'other'
  | 'probation'
  | 'do_not_use'
  | 'inactive'

export interface IVin {
  id?: number
  vin: string
  updatedAt?: string
  _destroy?: boolean
}

export interface ICarrierVinState {
  error?: string
  showDialog: boolean
  saving: boolean
  loading: boolean
  vins: IVin[]
  vinsBackup?: IVin[]
}
export interface IFactor {
  id: number
  name: string
  address: string
  address2: string
  city: string
  stateCode: string
  zipcode: string
  contactEmail?: string
  contactPhone?: string
}

export interface ICarrierAccounting {
  secondaryAccountingEmail?: string
  paymentMethod?: string
  quickPayEnrollment?: boolean
  factor?: IFactor
  carrierBillingLocation?: ICarrierBillingLocation
}

export interface ICarrierAccountingRequest extends ICarrierAccounting {
  factorId: number | null
  carrierBillingLocation: ICarrierBillingLocation
}

export interface ICarrierScore {
  id: number
  carrierApplicationId: number
  totalLoadsInScore: number
  trackingPct?: string
  notBouncePct?: string
  bounceRate: number
  otp30?: string
  otd30?: string
  carrierRatePctTtt?: string
  trackingPctBinned?: string
  notBouncePctBinned?: string
  otp30Binned?: string
  otd30Binned?: string
  carrierRatePctTttBinned?: string
  carrierScore?: string
  createdAt: string
  version: string
  rowLoadAt: string
}

export type ELD_TRACKING_METHOD = 'eld' | 'api' | 'mobile'

export interface ICarrier {
  isTrusted?: boolean
  id: string
  netsuiteId?: string
  state: TCarrierStatus
  status?: TCarrierStatus
  legalName: string
  insuranceAutoLiabilityCoverage: string
  carrierSpecificInfo: string
  automotiveInsurance: {
    policyType: string
  }
  carrierManagers?: ICarrierManager[]
  icon?: {
    Icon?: ({ className }: { className: string }) => JSX.Element
    color: string
  }
  mcNumber?: string
  dotNumber?: string
  carrierReferences: ICarrierReference[]
  totalShipmentsCount: number
  carrierScore?: ICarrierScore
  location?: ICarrierLocation
  contactEmail?: string
  isEldApproved?: boolean
  isEldApprovedUpdatedById?: string
  isEldApprovedUpdatedAt?: string
  eldTrackingMethod?: ELD_TRACKING_METHOD
  stateName: string
  bulkEmailEnabled?: boolean
  carrierBulkEmailRecipients: IDigestEmail[] | []
  taskAutomationConfig?: ICarrierTaskAutomationConfig
  keepTruckinEnabledUpdatedAt?: string
  keepTruckinEnabled?: boolean
}

export interface IDigestEmail {
  id?: string
  userId: string
  carrierApplicationId: string
  name: string
  userFullName?: string
  _destroy?: boolean
}

export interface ICarrierTaskAutomationConfig {
  canAutomateCheckEta: boolean
  canAutomateCheckin: boolean
  canAutomateCheckout: boolean
  canAutomateDispatch: boolean
}

export interface ICarrierResponsePayload {
  carrierApplication: ICarrier
}

export interface ICarrierReference {
  id?: number
  carrierApplicationId?: string
  contactName?: string
  companyName: string
  phone: string
  email?: string
  createdAt?: string
  updatedAt?: string
  position?: string
  reviewedById?: number | boolean
  reviewedAt?: string
  createdById?: number
  _destroy?: boolean
  reviewedBy?: {
    id: number
    parentUserId: number
    email: string
    firstName: string
    lastName: string
  }
  uuid: string
  phoneValidation?: string
  companyValidation?: string
}

export interface ICarrierDocumentsCheckListsState {
  checklists: {
    brokerCarrierAgreement?: {}
  }
}

export interface ICarrierReferencesState {
  references: ICarrierReference[]
  error?: string
  loading: boolean
  showDialog: boolean
}

export interface ICarrierState {
  isFetching: boolean
  error?: string
  carrierDetails?: ICarrier
  statusModal: boolean
  contactInstructionsModal: boolean
  contactInstructionsError?: string
  contactInstructionsSaving: boolean
  actionInProgress: boolean
  isFetchingEldTracking: boolean
  eldTrackingError?: string
  eldTrackingSuccess?: boolean
  taskAutomationConfigError: boolean
}

export enum SAFETY_RATING {
  conditional = 'CONDITIONAL',
  satisfactory = 'SATISFACTORY',
  unsatisfactory = 'UNSATISFACTORY',
  notRated = 'NOT RATED',
}

export interface IFMCSAInfo {
  id: number
  carrierApplicationId: number
  rating: SAFETY_RATING
  ratingDate?: string
  vehicleOutOfServicePercent: string
  driverOutOfServicePercent: string
  brokerAuthority?: string
  propertyAuthority?: string
  passengerAuthority?: string
  householdGoodsAuthority?: string
  operationClass?: string
  carrierOperations: string
  operatingStatus: string
  numPowerUnits: number
  numDrivers: number
  numFatalCrashes: number
  numInjuryCrashes: number
  phoneNumber: string
  updatedAt: string
  carrierApplication: ICarrier
}

export interface ICarrierFMCSAState {
  fmcsaInfo?: IFMCSAInfo
  loading: boolean
  error?: string
}

interface IBaseState {
  fetching: boolean
  error?: string
}

export interface ICarrierAccountingState extends IBaseState {
  accounting?: ICarrierAccounting
  updating: boolean
  showModal: boolean
}

export interface ICommodityState {
  commodities: ICommodity[]
  loading: boolean
  error?: string
  showDialog: boolean
}

export interface ICommodity {
  id: number
  description: string
  covered?: boolean | null
  verifiedBy: {
    id: number
    date: string
    name: string
  }
}

export enum ENTITY_TYPES {
  individual = 'Individual',
  ccorp = 'C Corporation',
  scorp = 'S Corporation',
  partnership = 'Partnership',
  trust = 'Trust',
  llc = 'LLC',
}

export interface IBaseCompanyInfo {
  legalName: string
  legalNameError?: string
  mcNumber: string
  dbaName: string
  dotNumber: string
  dotNumberError?: string
  entityType?: ENTITY_TYPES
  ein: string
  einError?: string
}

export interface ICompanyInfo extends IBaseCompanyInfo {
  // null represents unknown in the backend
  isCarbCompliant: boolean | null
  eldCompliant: boolean | null
  isDisadvantagedBusinessEntity: boolean | null
}

export interface ILocalCompanyInfo extends IBaseCompanyInfo {
  isCarbCompliant: 'yes' | 'no' | 'unknown'
  eldCompliant: 'yes' | 'no' | 'unknown'
  isDisadvantagedBusinessEntity: 'yes' | 'no' | 'unknown'
}

export interface ICompanyInfoState {
  info: ICompanyInfo
  loading: boolean
  updating: boolean
  error?: string
  dialogError?: string
  showDialog: boolean
}

export interface ICarrierContactState {
  contact: ICarrierContact
  loading: boolean
  updating: boolean
  error?: string
  dialogError?: string
  showDialog: boolean
}

export interface ICarrierLocation {
  id?: string
  address?: string
  addressLine2?: string
  city?: string
  stateCode?: string
  zipcode?: string
  latitude?: string
  longitude?: string
  contactPhone?: string
  contactPhoneExt?: string
  countryCode?: string
}

export interface ICarrierBillingLocation extends ICarrierLocation {
  contactEmail?: string
}

export interface ICarrierContact {
  contactEmail?: string
  location?: ICarrierLocation
  addressError?: string
  countryError?: string
  cityError?: string
  stateError?: string
  zipError?: string
}

export interface ICarrierCapacityState {
  capacities: ICarrierCapacity[]
  loading: boolean
  error?: string
  showDialog: boolean
  updating: boolean
  updateError?: string
}

export enum MANAGER_TYPES {
  primary = 'owner',
  secondary = 'secondary',
  primaryCe = 'ce_rep',
  secondaryCe = 'secondary_ce_rep',
}

export interface ICarrierManager {
  id: number
  email: string
  fullName: string
  phone: string
  relationshipType: MANAGER_TYPES
  role?: string
  userType?: string
  _destroy?: 'true'
  isPendingAdd?: boolean
}

export interface ILaneHistory {
  pickupCity: string
  pickupStateCode: string
  deliveryCity: string
  deliveryStateCode: string
  lastDispatcher: string
  lastRate: string
  shipmentCount: number
  equipmentName: string
  lastAssignedDate: string
}

export interface ILaneHistoryState extends IBaseState {
  lanes: ILaneHistory[]
  firstLoad: boolean
  totalCount: number
}

export enum RATE_TYPE {
  rpm = 'rpm',
  flat = 'flat',
}

export enum RATE_ACTION_TYPE {
  reserved = 'auto_offer',
  alert = 'lane_alert',
}

export interface ICarrierContractLaneEditData {
  contractId?: number
  carrierId: number
  rateType: 'radius' | 'lane'
  rate: string
  actionType: RATE_ACTION_TYPE
  deliveryRadiusMiles: string
  pickupRadiusMiles: string
  radiusRates: IRadiusRateEditEntry[]
  equipmentId: number
  isDatMarketApplicable: boolean
  laneId?: number
  loadType: string
  expirationDate: string
  startDate: string
  isOnlyBeer: boolean
  isOnlyDropTrailer: boolean
  carrierCapacity: ICarrierCapacity
  pickupLocation: {
    city: string
    state: string
    countryCode: string
  }
  deliveryLocation: {
    city: string
    state: string
    countryCode: string
  }
  dispatcher?: number
  dispatcherName?: string
  user?: {
    userType: string
    role: string
    firstName: string
    lastName: string
    email: string
    phone: string
    cellPhone: string
  }
  shipperId?: number
  isShipperExclusive?: boolean
  pickupTimeType: string
  pickupTimeEveryday: IPickupDayPreference
  pickupTimePreferences: IPickupDayPreference[]
  deliveryTimePreference: string
}

export interface IRadiusRateEditEntry {
  id?: number
  min: number
  max: number
  checked: boolean
  value: string
}

export interface IPickupDayPreference {
  startTime: string
  endTime: string
  checked?: boolean
  dayOfWeek: DayOfWeek
}

export interface ICarrierLanesState extends IBaseState {
  fetching: boolean
  lanes: {
    reserved: IRate[]
    alerts: IRate[]
  }
  actionInProgress: boolean
  actionError?: string
  showRemoveModal: boolean
  showEditModal: boolean
  showCreatedModal: boolean
  targetLane?: IRate
}

export interface ICarrierEquipment {
  key: string
  count: number
}

export interface ICarrierEquipmentState extends IBaseState {
  equipment?: ICarrierEquipment[]
  canDropTrailer?: boolean
}
